import type { SessionUser, SourceDashboard, SourceType } from "../types";

const DEFAULT_API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
const CONNECTION_SETTINGS_KEY = "navigation-plan-connection-settings";
const TOKEN_KEY = "navigation-plan-admin-token";

export type ConnectionSettings = {
  apiBase: string;
  pollIntervalSeconds: number;
  timeoutSeconds: number;
};

type RawSessionUser = Omit<SessionUser, "totpEnabled"> & { totpEnabled: unknown };

/**
 * The admin API owns the TOTP switch.  Be strict at the browser boundary so
 * legacy/proxied JSON such as { totpEnabled: "false" } cannot become truthy
 * and unexpectedly block an operator form.
 */
export function normalizeSessionUser(user: RawSessionUser): SessionUser {
  return { ...user, totpEnabled: user.totpEnabled === true };
}

function normalizeSessionResponse<T extends { user: RawSessionUser }>(response: T): Omit<T, "user"> & { user: SessionUser } {
  return { ...response, user: normalizeSessionUser(response.user) };
}

export type HealthReport = {
  ok: boolean;
  service: string;
  time: string;
  uptimeSeconds: number;
  api: { ok: boolean };
  database: {
    ok: boolean;
    latencyMs: number | null;
  };
  integrations: {
    easybot: IntegrationHeartbeat;
    game: IntegrationHeartbeat;
  };
};

export type IntegrationHeartbeat = {
      connected: boolean;
      lastSeenAt: string | null;
      ageMs: number | null;
      healthyWithinMs: number;
      metadata: Record<string, unknown> | null;
};

export type HealthCheckResult = {
  report: HealthReport;
  roundTripMs: number;
  checkedAt: string;
};

export type AdminAccount = {
  id: string;
  username: string;
  email: string | null;
  role: "OWNER" | "OPERATOR" | "AUDITOR";
  totpEnabled: boolean;
  createdAt: string;
};

export type PlayerSelectorOption = {
  uuid: string;
  name: string;
  reputation: number;
};

const defaultConnectionSettings: ConnectionSettings = {
  apiBase: DEFAULT_API_BASE,
  pollIntervalSeconds: 10,
  timeoutSeconds: 5
};

export function normalizeApiBase(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const candidate = /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmed)
    ? trimmed
    : `http://${trimmed}`;
  const url = new URL(candidate);
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error("API 地址仅支持 HTTP 或 HTTPS");
  }
  url.hash = "";
  url.search = "";
  return url.toString().replace(/\/$/, "");
}

export function getConnectionSettings(): ConnectionSettings {
  try {
    const saved = JSON.parse(localStorage.getItem(CONNECTION_SETTINGS_KEY) ?? "{}") as
      Partial<ConnectionSettings>;
    return {
      apiBase: typeof saved.apiBase === "string"
        ? normalizeApiBase(saved.apiBase)
        : defaultConnectionSettings.apiBase,
      pollIntervalSeconds: typeof saved.pollIntervalSeconds === "number"
        ? Math.min(120, Math.max(5, saved.pollIntervalSeconds))
        : defaultConnectionSettings.pollIntervalSeconds,
      timeoutSeconds: typeof saved.timeoutSeconds === "number"
        ? Math.min(15, Math.max(1, saved.timeoutSeconds))
        : defaultConnectionSettings.timeoutSeconds
    };
  } catch {
    return defaultConnectionSettings;
  }
}

export function saveConnectionSettings(settings: ConnectionSettings) {
  const normalized: ConnectionSettings = {
    apiBase: normalizeApiBase(settings.apiBase),
    pollIntervalSeconds: Math.min(120, Math.max(5, settings.pollIntervalSeconds)),
    timeoutSeconds: Math.min(15, Math.max(1, settings.timeoutSeconds))
  };
  localStorage.setItem(CONNECTION_SETTINGS_KEY, JSON.stringify(normalized));
  return normalized;
}

function getApiBase() {
  return getConnectionSettings().apiBase;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
  }
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  signal?: AbortSignal
): Promise<T> {
  const token = getToken();
  const response = await fetch(`${getApiBase()}${path}`, {
    ...options,
    signal,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    }
  });
  const json = await response.json().catch(() => ({})) as {
    error?: { message?: string };
  };
  if (!response.ok) {
    throw new ApiError(response.status, json.error?.message ?? `请求失败 (${response.status})`);
  }
  return json as T;
}

export async function checkHealth(
  settings: ConnectionSettings = getConnectionSettings()
): Promise<HealthCheckResult> {
  const controller = new AbortController();
  const timer = window.setTimeout(
    () => controller.abort(new DOMException("连接检测超时", "TimeoutError")),
    settings.timeoutSeconds * 1_000
  );
  const startedAt = performance.now();

  try {
    const response = await fetch(`${normalizeApiBase(settings.apiBase)}/health`, {
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal: controller.signal
    });
    const report = await response.json() as Partial<HealthReport>;
    if (
      typeof report.ok !== "boolean"
      || typeof report.service !== "string"
      || typeof report.api?.ok !== "boolean"
      || typeof report.database?.ok !== "boolean"
      || typeof report.integrations?.easybot?.connected !== "boolean"
      || typeof report.integrations?.game?.connected !== "boolean"
    ) {
      throw new Error(`服务返回了无效的健康报告 (${response.status})`);
    }
    return {
      report: report as HealthReport,
      roundTripMs: Math.max(0, Math.round(performance.now() - startedAt)),
      checkedAt: new Date().toISOString()
    };
  } finally {
    window.clearTimeout(timer);
  }
}

export async function getAuthConfig() {
  return request<{ emailCodeAvailable: boolean }>("/api/auth/config");
}

export async function login(identifier: string, password: string, totp?: string) {
  return normalizeSessionResponse(await request<{ token: string; user: RawSessionUser }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ identifier, password, totp: totp || undefined })
  }));
}

export async function sendEmailCode(email: string, purpose: "PASSWORD_RESET") {
  return request<{ accepted: boolean; message: string }>("/api/auth/email-code", {
    method: "POST",
    body: JSON.stringify({ email, purpose })
  });
}

export async function resetPassword(body: {
  email: string;
  password: string;
  code: string;
}) {
  return request<{ reset: true }>("/api/auth/password-reset", {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function me(signal?: AbortSignal) {
  return normalizeSessionResponse(await request<{ user: RawSessionUser }>("/api/auth/me", {}, signal));
}

export async function sendBindingEmailCode(email: string) {
  return request<{ accepted: boolean; message: string }>("/api/auth/email/code", {
    method: "POST",
    body: JSON.stringify({ email })
  });
}

export async function bindEmail(email: string, code: string) {
  return normalizeSessionResponse(await request<{ token: string; user: RawSessionUser }>("/api/auth/email", {
    method: "POST",
    body: JSON.stringify({ email, code })
  }));
}

export async function bindQq(qq: string) {
  return normalizeSessionResponse(await request<{ user: RawSessionUser }>("/api/auth/qq", {
    method: "POST",
    body: JSON.stringify({ qq })
  }));
}

export async function unbindQq() {
  return normalizeSessionResponse(await request<{ user: RawSessionUser }>("/api/auth/qq", {
    method: "DELETE"
  }));
}

export async function setupTotp() {
  return request<{ secret: string; otpauthUrl: string }>("/api/auth/totp/setup", {
    method: "POST"
  });
}

export async function enableTotp(secret: string, totp: string) {
  return normalizeSessionResponse(await request<{ token: string; user: RawSessionUser }>("/api/auth/totp/enable", {
    method: "POST",
    body: JSON.stringify({ secret, totp })
  }));
}

export async function disableTotp(password: string, totp: string) {
  return normalizeSessionResponse(await request<{ token: string; user: RawSessionUser }>("/api/auth/totp/disable", {
    method: "POST",
    body: JSON.stringify({ password, totp })
  }));
}

export async function listAdminAccounts() {
  return request<{ accounts: AdminAccount[] }>("/api/admin/accounts");
}

export async function createAdminAccount(body: {
  username: string;
  email?: string;
  password: string;
  role: "OPERATOR" | "AUDITOR";
  currentPassword: string;
  totp?: string;
}) {
  return request<{ account: AdminAccount }>("/api/admin/accounts", {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function updateAdminAccount(id: string, body: {
  username: string;
  email?: string;
  role: "OPERATOR" | "AUDITOR";
  password?: string;
  currentPassword: string;
  totp?: string;
}) {
  return request<{ account: AdminAccount }>(`/api/admin/accounts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body)
  });
}

export async function deleteAdminAccount(id: string, body: {
  currentPassword: string;
  totp?: string;
}) {
  return request<{ deleted: true }>(`/api/admin/accounts/${encodeURIComponent(id)}`, {
    method: "DELETE",
    body: JSON.stringify(body)
  });
}

export async function getDashboard(
  filter: {
    start?: string;
    end?: string;
    source?: SourceType;
    status?: string;
    riskStatus?: string;
    campaign?: string;
    inviter?: string;
    current?: number;
    pageSize?: number;
  },
  signal?: AbortSignal
) {
  const params = new URLSearchParams();
  if (filter.start) params.set("start", filter.start);
  if (filter.end) params.set("end", filter.end);
  if (filter.source) params.set("source", filter.source);
  if (filter.status) params.set("status", filter.status);
  if (filter.riskStatus) params.set("riskStatus", filter.riskStatus);
  if (filter.campaign) params.set("campaign", filter.campaign);
  if (filter.inviter) params.set("inviter", filter.inviter);
  if (filter.current) params.set("current", String(filter.current));
  if (filter.pageSize) params.set("pageSize", String(filter.pageSize));
  return request<SourceDashboard>(
    `/api/admin/source-dashboard${params.size ? `?${params}` : ""}`,
    {},
    signal
  );
}

export async function rebindPrimary(
  acquisitionId: string,
  body: {
    candidate: { playerId: number; uuid: string; name: string };
    reason: string;
    totp: string;
  }
) {
  return request(`/api/admin/acquisitions/${acquisitionId}/rebind`, {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function getBindingHistory(acquisitionId: string, signal?: AbortSignal) {
  return request<{
    binding: Record<string, unknown> | null;
    history: Array<Record<string, unknown>>;
  }>(`/api/admin/acquisitions/${acquisitionId}/binding-history`, {}, signal);
}

type PageRequest = {
  current?: number;
  pageSize?: number;
};

function pageParams(page: PageRequest) {
  const params = new URLSearchParams({
    current: String(page.current ?? 1),
    pageSize: String(page.pageSize ?? 20)
  });
  return params.toString();
}

export async function getRisks(page: PageRequest, signal?: AbortSignal) {
  return request<{ risks: Array<Record<string, unknown>>; total: number }>(
    `/api/admin/risks?${pageParams(page)}`,
    {},
    signal
  );
}

export async function getCampaigns(page: PageRequest, signal?: AbortSignal) {
  return request<{ campaigns: Array<Record<string, unknown>>; total: number }>(
    `/api/admin/campaigns?${pageParams(page)}`,
    {},
    signal
  );
}

export async function getAuditLogs(page: PageRequest, signal?: AbortSignal) {
  return request<{ logs: Array<Record<string, unknown>>; total: number }>(
    `/api/admin/audit-logs?${pageParams(page)}`,
    {},
    signal
  );
}

export async function getReputationLedger(page: PageRequest, signal?: AbortSignal) {
  return request<{ entries: Array<Record<string, unknown>>; total: number }>(
    `/api/admin/reputation-ledger?${pageParams(page)}`,
    {},
    signal
  );
}

export async function getRewards(page: PageRequest, signal?: AbortSignal) {
  return request<{ rewards: Array<Record<string, unknown>>; total: number }>(
    `/api/admin/rewards?${pageParams(page)}`,
    {},
    signal
  );
}

export async function requeueReward(
  id: string,
  body: { reason: string; confirmUnknown: boolean; totp?: string }
) {
  return request(`/api/admin/rewards/${id}/requeue`, {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function createCampaign(body: {
  name: string;
  code: string;
  source: "BILIBILI" | "DOUYIN";
  videoBvid?: string;
  startsAt?: string;
  endsAt?: string;
  maxRedemptions?: number;
}) {
  return request("/api/admin/campaigns", {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function resolveRisk(
  id: string,
  body: {
    status: "CLEARED" | "CONFIRMED";
    penaltyKind?: "WARNING" | "ABUSE" | "MUTE" | "FRAUD";
    totp?: string;
  }
) {
  return request(`/api/admin/risks/${id}/resolve`, {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function clearSharedIpRisks(riskIds: string[], totp?: string) {
  return request<{ cleared: number; riskIds: string[] }>("/api/admin/risks/batch-clear", {
    method: "POST",
    body: JSON.stringify({ riskIds, totp })
  });
}

export type GovernancePolicy = {
  initialScore: number;
  inviteRestrictedAt: number;
  muteAt: number;
  removeAt: number;
  recoveryHours: number;
  recoveryAmount: number;
};

export type ReputationRule = {
  id: string;
  code: string;
  name: string;
  description: string;
  trigger: "AUTO_INVITE" | "MANUAL";
  amount: number;
  enabled: boolean;
  builtIn: boolean;
  sortOrder: number;
};

export type ShopProduct = {
  id: string;
  sku: string;
  name: string;
  description: string;
  category: string;
  iconMaterial: string;
  priceCredits: number;
  deliveryType: "ITEM" | "VAULT_MONEY" | "PLAYERPOINTS" | "CONSOLE_COMMAND";
  payload: unknown;
  stock: number | null;
  perPlayerLimit: number | null;
  enabled: boolean;
  startsAt: string | null;
  endsAt: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export async function getGovernancePolicy() {
  return request<{ policy: GovernancePolicy }>("/api/admin/governance-policy");
}

export async function saveGovernancePolicy(body: GovernancePolicy & { totp: string }) {
  return request<{ policy: GovernancePolicy }>("/api/admin/governance-policy", {
    method: "PUT",
    body: JSON.stringify(body)
  });
}

export async function getReputationRules() {
  return request<{ rules: ReputationRule[] }>("/api/admin/reputation-credit/rules");
}

export async function createReputationRule(body: {
  code: string;
  name: string;
  description: string;
  amount: number;
  enabled: boolean;
  sortOrder: number;
  totp: string;
}) {
  return request<{ rule: ReputationRule }>("/api/admin/reputation-credit/rules", {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function updateReputationRule(id: string, body: {
  name: string;
  description: string;
  amount: number;
  enabled: boolean;
  sortOrder: number;
  totp: string;
}) {
  return request<{ rule: ReputationRule }>(`/api/admin/reputation-credit/rules/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body)
  });
}

export async function archiveReputationRule(id: string, totp: string) {
  return request<{ archived: true }>(`/api/admin/reputation-credit/rules/${encodeURIComponent(id)}`, {
    method: "DELETE",
    body: JSON.stringify({ totp })
  });
}

export async function grantReputationCredit(playerUuid: string, body: {
  ruleId: string;
  reference: string;
  reason: string;
  totp: string;
}) {
  return request(`/api/admin/players/${encodeURIComponent(playerUuid)}/reputation-credit/grant`, {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function adjustReputationCredit(playerUuid: string, body: {
  delta: number;
  reason: string;
  totp: string;
}) {
  return request(`/api/admin/players/${encodeURIComponent(playerUuid)}/reputation-credit/adjust`, {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function adjustGovernanceReputation(playerUuid: string, body: {
  delta: number;
  reason: string;
  totp: string;
}) {
  return request(`/api/admin/reputation/${encodeURIComponent(playerUuid)}/adjust`, {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function searchPlayerOptions(search = "") {
  const query = search.trim();
  const suffix = query ? `?search=${encodeURIComponent(query)}` : "";
  return request<{ players: PlayerSelectorOption[] }>(`/api/admin/players${suffix}`);
}

export async function getShopProducts(page: PageRequest = {}) {
  return request<{ products: ShopProduct[]; total: number }>(`/api/admin/shop/products?${pageParams(page)}`);
}

export type ShopProductInput = Omit<ShopProduct, "id" | "createdAt" | "updatedAt" | "startsAt" | "endsAt" | "stock" | "perPlayerLimit"> & {
  stock?: number | null;
  perPlayerLimit?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
};

export async function createShopProduct(body: ShopProductInput & { totp?: string }) {
  return request<{ product: ShopProduct }>("/api/admin/shop/products", {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function updateShopProduct(id: string, body: ShopProductInput & { totp?: string }) {
  return request<{ product: ShopProduct }>(`/api/admin/shop/products/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body)
  });
}

export async function deleteShopProduct(id: string, body: { totp?: string }) {
  return request<{ product: ShopProduct }>(`/api/admin/shop/products/${encodeURIComponent(id)}`, {
    method: "DELETE",
    body: JSON.stringify(body)
  });
}

export async function setShopProductAvailability(id: string, body: { enabled: boolean; stock?: number | null; totp?: string }) {
  return request<{ product: ShopProduct }>(`/api/admin/shop/products/${encodeURIComponent(id)}/availability`, {
    method: "PATCH",
    body: JSON.stringify(body)
  });
}

export async function getShopOrders(page: PageRequest = {}, playerUuid?: string) {
  const params = new URLSearchParams(pageParams(page));
  if (playerUuid) params.set("playerUuid", playerUuid);
  return request<{ orders: Array<Record<string, unknown>>; total: number }>(`/api/admin/shop/orders?${params}`);
}

export async function refundShopOrder(id: string, body: { reason: string; totp: string }) {
  return request(`/api/admin/shop/orders/${encodeURIComponent(id)}/refund`, {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function previewInviteCreditBackfill() {
  return request<{ count: number; amountPerInvite: number; total: number }>("/api/admin/reputation-credit/backfill/preview");
}

export async function executeInviteCreditBackfill(totp: string) {
  return request<{ credited: number }>("/api/admin/reputation-credit/backfill/execute", {
    method: "POST",
    body: JSON.stringify({ totp })
  });
}

export type OnboardingConfig = {
  welcomeText: string;
  mainGroupJoinText: string;
  modpackLink: string;
  modpackFileName: string;
  flowchartImageUrl: string;
  activityPosterImageUrl: string;
  managerQq: string;
  managerImageUrl: string;
  tripleVideoUrl: string;
  tripleGiftText: string;
  showManagerGiftText: boolean;
  managerGiftText: string;
  commentGiftText: string;
};

export type GiftReward = {
  items: Array<Record<string, unknown>>;
  itemLibraryIds: string[];
  commands: string[];
};

export type GiftItemLibraryEntry = {
  id: string;
  key: string;
  displayName: string;
  material: string;
  amount: number;
  lore: string[];
  item: Record<string, unknown>;
  updatedAt: string;
};

export type CommunityGiftConfig = {
  management: { enabled: boolean; reward: GiftReward };
  triple: { enabled: boolean; activeVideoId: string; reward: GiftReward };
  commentDanmaku: { enabled: boolean; activeVideoId: string; reward: GiftReward };
};

export type GiftClaim = {
  id: string;
  kind: "MANAGEMENT" | "TRIPLE" | "COMMENT_DANMAKU";
  qq: string;
  videoId: string | null;
  playerUuid: string | null;
  playerName: string | null;
  status: "PENDING_REVIEW" | "APPROVED" | "REJECTED" | "QUEUED_UNBOUND" | "DELIVERED";
  evidence: string | null;
  reviewReason: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
  reward?: { status: string; appliedAt: string | null; lastError: string | null } | null;
};

export async function getOnboardingConfig() {
  return request<{ draft: OnboardingConfig; published: OnboardingConfig }>("/api/admin/onboarding");
}

export async function saveOnboardingDraft(body: OnboardingConfig & { totp?: string }) {
  return request<{ draft: OnboardingConfig }>("/api/admin/onboarding/draft", {
    method: "PUT",
    body: JSON.stringify(body)
  });
}

export async function publishOnboarding(totp?: string) {
  return request<{ published: OnboardingConfig }>("/api/admin/onboarding/publish", {
    method: "POST",
    body: JSON.stringify({ totp })
  });
}

export async function getCommunityGiftConfig() {
  return request<{ draft: CommunityGiftConfig; published: CommunityGiftConfig }>("/api/admin/community-gifts");
}

export async function getGiftItemLibrary() {
  return request<{ items: GiftItemLibraryEntry[] }>("/api/admin/gift-item-library");
}

export async function saveCommunityGiftDraft(body: CommunityGiftConfig & { totp?: string }) {
  return request<{ draft: CommunityGiftConfig }>("/api/admin/community-gifts/draft", {
    method: "PUT",
    body: JSON.stringify(body)
  });
}

export async function publishCommunityGifts(totp?: string) {
  return request<{ published: CommunityGiftConfig }>("/api/admin/community-gifts/publish", {
    method: "POST",
    body: JSON.stringify({ totp })
  });
}

export async function getGiftClaims(filter: {
  kind?: GiftClaim["kind"];
  status?: GiftClaim["status"];
  current?: number;
  pageSize?: number;
} = {}) {
  const params = new URLSearchParams();
  if (filter.kind) params.set("kind", filter.kind);
  if (filter.status) params.set("status", filter.status);
  params.set("current", String(filter.current ?? 1));
  params.set("pageSize", String(filter.pageSize ?? 100));
  return request<{ claims: GiftClaim[]; total: number }>(`/api/admin/community-gifts/claims?${params}`);
}

export async function createCommunityGiftClaim(body: {
  kind: "MANAGEMENT" | "COMMENT_DANMAKU";
  qq: string;
  videoId?: string;
  evidence?: string;
  externalRef?: string;
  totp?: string;
}) {
  return request<{ claim: GiftClaim; duplicate: boolean }>("/api/admin/community-gifts/claims", {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function reviewCommunityGiftClaim(id: string, body: { approve: boolean; reason?: string; totp?: string }) {
  return request<{ claim: GiftClaim }>(`/api/admin/community-gifts/claims/${encodeURIComponent(id)}/review`, {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function approveCommunityGiftClaims(ids: string[], totp?: string) {
  return request<{
    result: {
      requested: number;
      approved: number;
      queued: number;
      failed: Array<{ id: string; message: string }>;
    };
  }>("/api/admin/community-gifts/claims/batch-approve", {
    method: "POST",
    body: JSON.stringify({ ids, totp })
  });
}

export async function importCommentDanmakuCsv(csv: string, totp?: string) {
  return request<{ imported: number; results: Array<{ responseId: string; qq: string; result: string; reason?: string }> }>(
    "/api/admin/community-gifts/comment-csv",
    { method: "POST", body: JSON.stringify({ csv, totp }) }
  );
}
